import { Outlet, useNavigate } from 'react-router';
import React, { useEffect, useMemo } from 'react';
import { useHandleCobrandChange } from 'features/cobrand/hooks/useHandleCobrandChange';
import { useAppDispatch } from 'store/store';
import { useCobrand } from 'features/cobrand/hooks/useCobrandUtils';
import { useIsLoggedIn } from 'features/auth/hooks/useAuthData';
import { selectClientCobrand, selectServerCobrandSku } from 'features/cobrand/cobrandSlice';
import { getCobrandFromHostName, isValidCobrand } from 'features/cobrand/utils/cobrandUtils';
import { NotFound } from 'screens/NotFound';
import { logoutThunk } from 'features/auth/thunks/logoutThunk';
import { useSelector } from 'react-redux';

export const CobrandGuard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useIsLoggedIn();
  const { rawPathParam } = useCobrand();
  const serverSku = useSelector(selectServerCobrandSku);
  const clientCobrand = useSelector(selectClientCobrand);

  // Check if hostname matches a cobrand (for personal/local env with hostname routing)
  // Memoize to prevent unnecessary recalculation on every render
  const hostnameCobrand = useMemo(() => getCobrandFromHostName(), []);

  useEffect(() => {
    // Detect if the user is on an incorrect cobrand based on the URL path and server response
    const isIncorrectCobrand = serverSku && clientCobrand && clientCobrand.sku !== serverSku;
    if (isLoggedIn && isIncorrectCobrand) {
      dispatch(logoutThunk());
    }
  }, [isLoggedIn, clientCobrand, serverSku, dispatch]);

  // CareFirst-specific redirect: caremanagement.carefirst.com/home → caremanagement.carefirst.com/carefirst/home
  useEffect(() => {
    if (
      window.location.hostname.toLowerCase().includes('carefirst') &&
      !window.location.pathname.includes('/carefirst')
    ) {
      navigate('/carefirst', { replace: true });
    }
  }, [navigate]);

  useHandleCobrandChange();

  // If hostname matches a cobrand, allow routing (handles local env like caremanagement.carefirst.com:3000/home)
  // Otherwise, validate path parameter (handles staging/prod like member.staging.wellframe.com/carefirst/home)
  return hostnameCobrand || isValidCobrand(rawPathParam) || !rawPathParam ? (
    <Outlet />
  ) : (
    <NotFound />
  );
};
